import { FormEvent, useEffect, useState } from "react";
import { apiCreateTransaction, type AccountRow, type CategoryRow } from "../api";
import { todayLocalISO } from "../utils/money";

const OPTION_LABEL_MAX = 42;

function optionLabel(s: string, max = OPTION_LABEL_MAX): string {
  const t = s.trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

type Props = {
  accounts: AccountRow[];
  categories: CategoryRow[];
  onCreated: () => void | Promise<void>;
  /** When false, date is always today (overview quick add). */
  showDate?: boolean;
  title?: string;
  hint?: string | null;
  /** Omit outer card (parent supplies layout, e.g. Overview quick-add panel). */
  compactShell?: boolean;
};

export function AddTransactionForm({
  accounts,
  categories,
  onCreated,
  showDate = true,
  title = "Add transaction",
  hint = null,
  compactShell = false,
}: Props) {
  const [accountId, setAccountId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [occurredOn, setOccurredOn] = useState(todayLocalISO);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (accounts.length && !accounts.some((a) => a.id === accountId)) {
      setAccountId(accounts[0]!.id);
    }
  }, [accounts, accountId]);

  useEffect(() => {
    if (categories.length && !categories.some((c) => c.id === categoryId)) {
      setCategoryId(categories[0]!.id);
    }
  }, [categories, categoryId]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    const amt = Number(amount);
    if (!accountId || !categoryId) {
      setErr("Choose an account and category.");
      return;
    }
    if (!Number.isFinite(amt) || amt <= 0) {
      setErr("Enter a positive amount.");
      return;
    }
    const date = showDate ? occurredOn : todayLocalISO();
    setBusy(true);
    try {
      await apiCreateTransaction({
        account_id: accountId,
        category_id: categoryId,
        amount: amt,
        description: description.trim(),
        occurred_on: date,
      });
      setAmount("");
      setDescription("");
      if (showDate) setOccurredOn(todayLocalISO());
      await onCreated();
    } catch (er) {
      setErr(er instanceof Error ? er.message : "Error");
    } finally {
      setBusy(false);
    }
  }

  if (accounts.length === 0 || categories.length === 0) {
    return null;
  }

  const inner = (
    <>
      <h3 className="mt-0">{title}</h3>
      {hint ? <p className="muted small mb-0">{hint}</p> : null}
      {err ? <p className="error">{err}</p> : null}
      <form className="txn-add-form" onSubmit={(e) => void onSubmit(e)}>
        <div className="txn-add-row">
          {showDate ? (
            <label>
              Date
              <input
                type="date"
                value={occurredOn}
                onChange={(e) => setOccurredOn(e.currentTarget.value)}
                required
              />
            </label>
          ) : null}
          <label>
            Account
            <select value={accountId} onChange={(e) => setAccountId(e.currentTarget.value)}>
              {accounts.map((a) => (
                <option key={a.id} value={a.id} title={a.name}>
                  {optionLabel(a.name)}
                </option>
              ))}
            </select>
          </label>
          <label>
            Category
            <select value={categoryId} onChange={(e) => setCategoryId(e.currentTarget.value)}>
              {categories.map((c) => {
                const full = `${c.name} (${c.kind})`;
                return (
                  <option key={c.id} value={c.id} title={full}>
                    {optionLabel(`${c.name} (${c.kind})`)}
                  </option>
                );
              })}
            </select>
          </label>
          <label className="txn-add-amount">
            Amount
            <input
              inputMode="decimal"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.currentTarget.value)}
              required
            />
          </label>
        </div>
        <label className="txn-add-desc">
          Description
          <input
            value={description}
            onChange={(e) => setDescription(e.currentTarget.value)}
            placeholder="Optional"
          />
        </label>
        <div className="txn-add-actions">
          <button type="submit" disabled={busy}>
            {busy ? "Saving…" : "Add transaction"}
          </button>
        </div>
      </form>
    </>
  );

  if (compactShell) {
    return <div className="quick-add-embedded">{inner}</div>;
  }

  return <div className="card">{inner}</div>;
}
